import { Outlet, Link, useLocation } from "react-router-dom";
import { useAuth, type AssignedUser } from "@/contexts/AuthContext";
import { logout } from "@/lib/auth";
import { setActingAsUserId } from "@/lib/api";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard, CreditCard, Settings, Variable, Image, Type,
  FileText, Gift, Users, BarChart3, Calendar, Link2, BookOpen,
  Key, LogOut, Menu, X, ChevronDown, AlertTriangle
} from "lucide-react";
import { useState, useEffect } from "react";

const adminNav = [
  { label: "Dashboard", path: "/admin", icon: LayoutDashboard, exact: true },
  { label: "Users", path: "/admin/users", icon: Users },
  { label: "Cards (All)", path: "/admin/cards", icon: CreditCard },
  { label: "Config", path: "/admin/config", icon: Settings },
  { label: "Variables", path: "/admin/variables", icon: Variable },
  { label: "Media", path: "/admin/media", icon: Image },
  { label: "Fonts", path: "/admin/fonts", icon: Type },
  { label: "Forms", path: "/admin/forms", icon: FileText },
  { label: "Redemptions", path: "/admin/redemptions", icon: Gift },
  { label: "Analytics", path: "/admin/analytics", icon: BarChart3 },
];

const userNav = [
  { label: "My Cards", path: "/dashboard", icon: CreditCard, exact: true },
  { label: "Calendar", path: "/dashboard/calendar", icon: Calendar },
  { label: "Events", path: "/dashboard/events", icon: BookOpen },
  { label: "Share Links", path: "/dashboard/share-links", icon: Link2 },
  { label: "Booking Pages", path: "/dashboard/booking-pages", icon: BookOpen },
  { label: "API Keys", path: "/dashboard/api-keys", icon: Key },
];

type LayoutSection = "admin" | "dashboard" | "calendar";

export default function AdminLayout({ section = "admin" }: { section?: LayoutSection }) {
  const { user, actingAs, setActingAs, assignedUsers } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userSwitcherOpen, setUserSwitcherOpen] = useState(false);

  // Sync actingAs to API client whenever it changes
  useEffect(() => {
    setActingAsUserId(actingAs?.id ?? null);
  }, [actingAs]);

  const role = user?.role || "user";

  let nav: typeof adminNav;
  let sectionTitle: string;

  if (section === "admin" && role === "admin") {
    nav = adminNav;
    sectionTitle = "card.buzz Admin";
  } else if (section === "calendar") {
    nav = userNav; // calendar uses same nav for now
    sectionTitle = "Calendar";
  } else {
    nav = userNav;
    sectionTitle = role === "secretary" ? "Secretary Panel" : "My Dashboard";
  }

  const isActive = (path: string, exact?: boolean) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleSelectUser = (assignedUser: AssignedUser) => {
    setActingAs(assignedUser);
    setUserSwitcherOpen(false);
  };

  // Secretary warning: no user selected
  const secretaryNoUser = role === "secretary" && !actingAs && section !== "admin";

  const roleBadgeClass =
    role === "admin" ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" :
    role === "secretary" ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" :
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400";

  return (
    <div className="flex h-screen bg-background">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 bg-sidebar border-r border-sidebar-border flex flex-col transition-transform duration-200 lg:relative lg:translate-x-0",
        sidebarOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-sidebar-border shrink-0">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center shadow-sm">
              <span className="text-white font-bold text-sm">CB</span>
            </div>
            <span className="font-semibold text-sidebar-foreground text-sm">{sectionTitle}</span>
          </Link>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-1.5 rounded-lg hover:bg-sidebar-accent">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Section switcher tabs */}
        <div className="px-3 pt-3 shrink-0">
          <div className="flex gap-1 bg-muted rounded-lg p-1">
            {role === "admin" && (
              <Link to="/admin" className={cn(
                "flex-1 text-center py-1.5 text-xs font-medium rounded-md transition-colors",
                section === "admin" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              )}>Admin</Link>
            )}
            {(role === "user" || role === "secretary") && (
              <Link to="/dashboard" className={cn(
                "flex-1 text-center py-1.5 text-xs font-medium rounded-md transition-colors",
                section === "dashboard" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
              )}>Dashboard</Link>
            )}
            <Link to={role === "admin" ? "/admin/calendar" : "/dashboard/calendar"} className={cn(
              "flex-1 text-center py-1.5 text-xs font-medium rounded-md transition-colors",
              section === "calendar" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
            )}>Calendar</Link>
          </div>
        </div>

        {/* Secretary user-switcher */}
        {role === "secretary" && (
          <div className="px-3 pt-2 shrink-0">
            <div className="relative">
              <button
                onClick={() => setUserSwitcherOpen(!userSwitcherOpen)}
                className={cn(
                  "w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm border transition-colors",
                  actingAs
                    ? "bg-primary/8 border-primary/20 hover:bg-primary/12"
                    : "bg-amber-50 border-amber-200 hover:bg-amber-100 dark:bg-amber-900/20 dark:border-amber-700"
                )}
              >
                <div className={cn(
                  "w-6 h-6 rounded-full flex items-center justify-center shrink-0 text-xs font-bold",
                  actingAs ? "bg-primary/20 text-primary" : "bg-amber-200 text-amber-700 dark:bg-amber-700 dark:text-amber-200"
                )}>
                  {actingAs ? (actingAs.name?.charAt(0)?.toUpperCase() || "U") : "?"}
                </div>
                <span className={cn("flex-1 text-left truncate text-sm font-medium", !actingAs && "text-amber-700 dark:text-amber-400")}>
                  {actingAs ? (actingAs.name || actingAs.email) : "Select a user..."}
                </span>
                <ChevronDown className={cn(
                  "w-4 h-4 shrink-0 transition-transform",
                  userSwitcherOpen && "rotate-180",
                  actingAs ? "text-muted-foreground" : "text-amber-500"
                )} />
              </button>

              {userSwitcherOpen && (
                <div className="absolute top-full left-0 right-0 mt-1.5 bg-popover border border-border rounded-xl shadow-lg z-20 overflow-hidden">
                  {assignedUsers.length === 0 ? (
                    <div className="px-4 py-3 text-sm text-muted-foreground text-center">
                      <p className="font-medium">No users assigned</p>
                      <p className="text-xs mt-0.5">Ask your admin to assign users to you</p>
                    </div>
                  ) : (
                    <div className="py-1">
                      {assignedUsers.map((u) => (
                        <button
                          key={u.id}
                          onClick={() => handleSelectUser(u)}
                          className={cn(
                            "w-full flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-accent transition-colors text-left",
                            actingAs?.id === u.id && "bg-accent"
                          )}
                        >
                          <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <span className="text-xs font-semibold text-primary">
                              {u.name?.charAt(0)?.toUpperCase() || "U"}
                            </span>
                          </div>
                          <div className="min-w-0">
                            <p className="truncate font-medium">{u.name || "Unnamed"}</p>
                            <p className="truncate text-xs text-muted-foreground">{u.email}</p>
                          </div>
                          {actingAs?.id === u.id && (
                            <div className="ml-auto w-2 h-2 rounded-full bg-primary shrink-0" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-3 space-y-0.5 mt-1">
          {nav.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path, item.exact);
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  active
                    ? "bg-sidebar-accent text-sidebar-accent-foreground"
                    : "text-sidebar-foreground/70 hover:bg-sidebar-accent/50 hover:text-sidebar-foreground"
                )}
              >
                <Icon className="w-4 h-4 shrink-0" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* User footer */}
        <div className="p-3 border-t border-sidebar-border shrink-0">
          <div className="flex items-center gap-3 px-2 py-1.5 rounded-lg">
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <span className="text-xs font-semibold text-primary">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <p className="text-sm font-medium text-sidebar-foreground truncate">{user?.name || "User"}</p>
                <span className={cn("text-[10px] font-semibold px-1.5 py-0.5 rounded-full uppercase", roleBadgeClass)}>
                  {role}
                </span>
              </div>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
            </div>
            <button
              onClick={logout}
              className="p-1.5 rounded-lg hover:bg-sidebar-accent text-muted-foreground hover:text-destructive transition-colors"
              title="Sign out"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile top bar */}
        <header className="h-16 border-b border-border flex items-center px-4 lg:hidden shrink-0">
          <button onClick={() => setSidebarOpen(true)} className="p-2 rounded-lg hover:bg-accent">
            <Menu className="w-5 h-5" />
          </button>
          <span className="ml-3 font-semibold text-sm">{sectionTitle}</span>
          {role === "secretary" && actingAs && (
            <span className="ml-auto text-xs text-muted-foreground truncate max-w-[120px]">
              {actingAs.name || actingAs.email}
            </span>
          )}
        </header>

        {/* Secretary: no user selected warning */}
        {secretaryNoUser && (
          <div className="mx-4 mt-4 lg:mx-6 flex items-start gap-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl px-4 py-3 shrink-0">
            <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">No user selected</p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                {assignedUsers.length === 0
                  ? "No users have been assigned to you yet. Contact your admin."
                  : "Select a user from the sidebar to manage their cards and calendar."}
              </p>
            </div>
          </div>
        )}

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
